import { isValidElement, useEffect, useMemo } from "react";
import { message } from "antd";

import "../styles/appToast.css";

/**
 * Keeps a single toast on screen app-wide: opening a new one closes whatever is
 * still showing, no matter which screen or modal opened it.
 *
 * antd hands every `message.useMessage()` caller its own isolated container, so
 * a component can only clear toasts it opened itself -- which is why messages
 * from different screens stack up. This module registers every live instance
 * (and the keys opened through the static API) so any of them can clear the
 * rest.
 *
 * The outgoing toast is closed outright rather than left to fade: appToast.css
 * cuts the leave animation so the replacement does not render below a notice on
 * its way out and then jump up into its place.
 *
 * Nothing else about the toast changes: type, content, duration, onClose and
 * every other antd option are passed straight through.
 */

// Live instances returned by `useAppMessage`, keyed by the antd api object.
const instances = new Set();

// Keys opened through `appMessage` (the static antd API). Tracked so they can be
// closed one at a time: a bare `message.destroy()` tears down the shared
// container, which would swallow a toast opened in the same tick.
const staticKeys = new Set();

let keySeed = 0;

/** Closes every toast currently on screen, wherever it was opened from. */
export const dismissAllToasts = () => {
  instances.forEach((api) => api.destroy());

  if (staticKeys.size > 0) {
    staticKeys.forEach((key) => message.destroy(key));
    staticKeys.clear();
  }
};

const OPEN_METHODS = ["open", "success", "error", "warning", "info", "loading"];

/**
 * antd accepts either `(content, duration?, onClose?)` or a single config
 * object. Normalise both so the static wrapper can attach its own key without
 * changing what the caller asked for.
 */
const toConfig = (args) => {
  const [content, duration, onClose] = args;

  // A config object -- but not a React element, which is valid `content`.
  if (content && typeof content === "object" && !isValidElement(content)) {
    return { ...content };
  }

  const config = { content };

  if (typeof duration === "function") {
    config.onClose = duration;
  } else {
    if (duration !== undefined) config.duration = duration;
    if (typeof onClose === "function") config.onClose = onClose;
  }

  return config;
};

/** Wraps an antd message API so each open clears the toasts already showing. */
const withDismissAll = (api, trackKeys) => {
  const wrapped = { destroy: (key) => api.destroy(key) };

  OPEN_METHODS.forEach((name) => {
    wrapped[name] = (...args) => {
      dismissAllToasts();

      if (!trackKeys) return api[name](...args);

      const config = toConfig(args);
      const key = config.key ?? `app-toast-${(keySeed += 1)}`;
      const { onClose } = config;

      staticKeys.add(key);

      return api[name]({
        ...config,
        key,
        onClose: () => {
          staticKeys.delete(key);
          onClose?.();
        },
      });
    };
  });

  return wrapped;
};

/**
 * Drop-in replacement for `message.useMessage()`.
 *
 * Returns `[messageApi, contextHolder]` with the same shape antd returns, and
 * `messageApi` keeps a stable identity so it stays safe in dependency arrays.
 */
export const useAppMessage = () => {
  const [api, contextHolder] = message.useMessage();

  useEffect(() => {
    instances.add(api);
    return () => {
      instances.delete(api);
    };
  }, [api]);

  const messageApi = useMemo(() => withDismissAll(api, false), [api]);

  return [messageApi, contextHolder];
};

/**
 * Same behaviour for the screens that use the static antd API instead of the
 * hook, where the toast has to outlive the component that opened it (a message
 * shown just before navigating away).
 */
export const appMessage = withDismissAll(message, true);

export default useAppMessage;
